import { useState, FormEvent } from 'react';

interface LoginPageProps {
  onLogin: (password: string) => void;
  error: string;
  loading: boolean;
}

export default function LoginPage({ onLogin, error, loading }: LoginPageProps) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (password.trim()) {
      onLogin(password);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
      <div className="w-full max-w-sm">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 flex items-center justify-center bg-emerald-500 rounded-2xl mx-auto mb-4">
            <i className="ri-shield-keyhole-line text-white text-2xl" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Dashboard Access</h1>
          <p className="text-sm text-gray-500 mt-1">Enter your password to continue</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoFocus
                  className={`w-full px-4 py-3 pr-11 rounded-lg border text-sm outline-none transition-colors
                    ${error
                      ? 'border-red-300 focus:border-red-400 bg-red-50'
                      : 'border-gray-200 focus:border-emerald-400 bg-gray-50 focus:bg-white'
                    } text-gray-900 placeholder:text-gray-400`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
              {error && (
                <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  {error}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !password.trim()}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line" />
                  Access Dashboard
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Private dashboard &mdash; authorized access only
        </p>
      </div>
    </div>
  );
}
